import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Linkedin,
  Sparkles,
  Save,
  Send,
  Copy,
  Check,
  Clock,
  Loader2,
  AlertTriangle,
  Lock,
  Shield,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { useOutreachSenders } from "@/hooks/useOutreachSenders";
import { useCampaignSender } from "@/hooks/useCampaignSender";
import { useSenderConnection } from "@/hooks/useSenderConnection";
import { useContactDetailFull } from "@/hooks/useContactDetail";
import { MessageContextPane } from "./MessageContextPane";
import type {
  OutreachSender,
  OutreachGenerationPayload,
  SavedOutreachMessage,
} from "@/types/outreach";
import type { Json } from "@/integrations/supabase/types";

// ── Props ────────────────────────────────────────────────────────────

interface LinkedInFollowUpPanelProps {
  contactId: string;
  campaignId?: string | null;
}

// ── Hook: existing LI message ────────────────────────────────────────

function useExistingLinkedInMessage(contactId: string, campaignId?: string | null) {
  return useQuery({
    queryKey: ["linkedin-followup-message", contactId, campaignId],
    queryFn: async (): Promise<SavedOutreachMessage | null> => {
      let query = (supabase as any)
        .from("outreach_messages")
        .select("*")
        .eq("contact_id", contactId)
        .eq("message_type", "linkedin_connection_followup");

      if (campaignId) {
        query = query.eq("campaign_id", campaignId);
      }

      const { data, error } = await query
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as SavedOutreachMessage | null;
    },
    staleTime: 30_000,
  });
}

// ── Component ────────────────────────────────────────────────────────

export function LinkedInFollowUpPanel({ contactId, campaignId }: LinkedInFollowUpPanelProps) {
  const queryClient = useQueryClient();
  const { data: contact } = useContactDetailFull(contactId);
  const { data: existingMsg, isLoading: loadingMsg } = useExistingLinkedInMessage(contactId, campaignId);
  const { senders, isLoading: loadingSenders, resolvedSenderId, isAdmin } = useOutreachSenders();
  const { data: campaignSender, isLoading: loadingCampaign } = useCampaignSender(campaignId, senders);
  const userSender = senders.find((s) => s.id === resolvedSenderId) ?? null;
  // Admin status and "has a sending persona" are independent. Previously this read
  // `isAdmin && !resolvedSenderId`, which silently revoked admin rights from any admin
  // who was also a rep (Jacob, bethanne@) and dropped them into the read-only branch.
  // In admin mode the Send button is intentionally withheld further down: an admin may
  // draft for any rep, but only that rep can send from their own LinkedIn account.
  const isAdminMode = isAdmin;

  // ── Local state (must be before derived values that use selectedSenderId) ──
  const [selectedSenderId, setSelectedSenderId] = useState<string>("");
  const variant = "traditional" as const;
  const [messageText, setMessageText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedMessageId, setSavedMessageId] = useState<string | null>(null);
  const [savedStatus, setSavedStatus] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [generationMeta, setGenerationMeta] = useState<Record<string, any> | null>(null);

  // Contact classification feeds the left rail BEFORE a draft exists (willUse).
  const sd = contact?.segment_data ?? null;
  const willUseCtx = sd
    ? {
        pain_points: sd.primary_pain_points ?? undefined,
        value_propositions: sd.value_hook ? [sd.value_hook] : undefined,
      }
    : null;

  const activeSender = isAdminMode ? senders.find((s) => s.id === selectedSenderId) ?? null : userSender;
  const connectionEnabled = !!activeSender?.heyreachAccountId && !!contact?.linkedin_url;
  const { data: connectionCheck } = useSenderConnection(
    activeSender?.heyreachAccountId,
    contact?.linkedin_url,
    connectionEnabled,
  );

  // ── Seed from existing message ──
  useEffect(() => {
    if (existingMsg) {
      setMessageText(existingMsg.final_text || existingMsg.generated_text);
      setSavedMessageId(existingMsg.id);
      setSavedStatus(existingMsg.status);
      setGenerationMeta(existingMsg.generation_metadata as Record<string, any> || null);
    }
  }, [existingMsg]);

  // ── Preselect a sender ──
  // Non-admins are locked to their own persona. Admins get a sensible default they can
  // change: the campaign's own sender first, then their persona, then the first sender.
  useEffect(() => {
    if (selectedSenderId) return;
    if (!isAdminMode) {
      if (resolvedSenderId) setSelectedSenderId(resolvedSenderId);
      return;
    }
    // Wait for the campaign's owner to resolve before seeding. Seeding early would
    // latch the admin's OWN persona (this effect only runs once), leaving them
    // selected as themselves on another rep's row — which would wrongly re-enable
    // Send and attribute the message to the wrong LinkedIn account.
    if (loadingCampaign) return;
    const preferred = campaignSender?.senderId ?? resolvedSenderId ?? senders[0]?.id ?? "";
    if (preferred) setSelectedSenderId(preferred);
  }, [resolvedSenderId, isAdminMode, senders, selectedSenderId, campaignSender, loadingCampaign]);

  const selectedSender = senders.find((s) => s.id === selectedSenderId);
  const isMyConnection = connectionCheck?.status === 'connected';
  const isMyCampaign = !!campaignId && !!resolvedSenderId && campaignSender?.senderId === resolvedSenderId;
  const canAccessPanel = isAdminMode || !!userSender;
  // Send is gated on identity, NOT on admin status. An admin who is also a rep
  // (Jacob, bethanne@) must keep Send on their OWN queue — it's their daily job.
  // They lose it only while drafting as someone else, because the message would
  // go out from that rep's LinkedIn account under that rep's name.
  const isDraftingAsSelf = !!resolvedSenderId && selectedSenderId === resolvedSenderId;
  const canSend = isDraftingAsSelf;
  // Why the panel is unavailable, when it is. Surfaced to the user instead of rendering nothing.
  const blockedReason = canAccessPanel
    ? null
    : isAdmin
      ? "No sending profiles are configured yet."
      : "Your login isn't linked to a sending profile yet, so drafting is unavailable. Ask an admin to connect your account to your LinkedIn sender.";
  const charCount = messageText.length;
  const maxChars = 900; // Hard limit — messages over 900 fail delivery via LinkedIn automation
  const isOverLimit = charCount > maxChars;

  // ── Generate ──
  const handleGenerate = useCallback(async () => {
    if (!selectedSender || !contact) return;

    setIsGenerating(true);
    try {
      const payload: OutreachGenerationPayload = {
        messageType: "linkedin_connection_followup",
        messagingVariant: variant,
        sender: {
          name: selectedSender.name,
          headline: selectedSender.headline || undefined,
          about: selectedSender.about || undefined,
          positionDescription: selectedSender.positionDescription || undefined,
        },
        contact: {
          name: contact.full_name || "Contact",
          firstName: contact.first_name || undefined,
          title: contact.job_title || undefined,
          headline: contact.clay_headline || undefined,
          summary: contact.clay_summary || undefined,
          seniority: contact.carr_seniority || undefined,
          department: contact.carr_department || undefined,
          roleType: contact.marketing_persona || undefined,
          location: contact.location || contact.clay_location || undefined,
          clayHeadline: contact.clay_headline || undefined,
          claySummary: contact.clay_summary || undefined,
          marketingPersona: contact.marketing_persona || undefined,
          carrSeniority: contact.carr_seniority || undefined,
          carrDepartment: contact.carr_department || undefined,
        },
        account: {
          name: contact.company_name || "Unknown",
          domain: contact.company_domain || undefined,
          description: contact.company_description || undefined,
          category: contact.company_primary_category || undefined,
        },
        senderProfileId: selectedSender.id,
      };

      const { data, error } = await supabase.functions.invoke("generate-outreach-message", {
        body: payload,
      });

      if (error) throw error;
      if (data?.success && data?.message) {
        setMessageText(data.message);
        setGenerationMeta(data.metadata || null);
        toast.success("Follow-up generated");
      } else {
        throw new Error(data?.error || "No message generated");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Generation failed";
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  }, [selectedSender, contact, variant]);

  // ── Save Draft ──
  const handleSave = useCallback(async () => {
    if (!messageText.trim() || !selectedSender || !contact) return;
    setIsSaving(true);

    try {
      if (savedMessageId) {
        const { error } = await supabase
          .from("outreach_messages")
          .update({
            final_text: messageText,
            sender_id: selectedSender.id,
            sender_name: selectedSender.canonicalName,
            messaging_variant: variant,
            role_type_used: generationMeta?.marketingPersona ?? null,
            messaging_angle: generationMeta?.messagingAngle ?? null,
            account_category: generationMeta?.accountCategory ?? null,
            matrix_source: generationMeta?.messagingSource ? (String(generationMeta.messagingSource).includes("fallback") ? "fallback" : "matrix") : null,
            generation_metadata: (generationMeta || {}) as unknown as Json,
            updated_at: new Date().toISOString(),
          })
          .eq("id", savedMessageId);
        if (error) throw error;
        toast.success("Draft updated");
      } else {
        const { data, error } = await supabase
          .from("outreach_messages")
          .insert({
            contact_id: contactId,
            contact_name: contact.full_name || "Contact",
            contact_title: contact.job_title || null,
            account_id: contact.company_id || null,
            account_name: contact.company_name || null,
            sender_id: selectedSender.id,
            sender_name: selectedSender.canonicalName,
            message_type: "linkedin_connection_followup",
            messaging_variant: variant,
            generated_text: messageText,
            final_text: messageText,
            status: "draft",
            campaign_id: campaignId || null,
            contact_linkedin_url: contact.linkedin_url || null,
            delivery_channel: "linkedin_automation",
            role_type_used: generationMeta?.marketingPersona ?? null,
            messaging_angle: generationMeta?.messagingAngle ?? null,
            account_category: generationMeta?.accountCategory ?? null,
            matrix_source: generationMeta?.messagingSource ? (String(generationMeta.messagingSource).includes("fallback") ? "fallback" : "matrix") : null,
            generation_metadata: (generationMeta || {}) as unknown as Json,
          })
          .select("id")
          .single();
        if (error) throw error;
        const inserted = data as unknown as { id: string };
        setSavedMessageId(inserted.id);
        setSavedStatus("draft");
        toast.success("Draft saved");
      }

      queryClient.invalidateQueries({ queryKey: ["linkedin-followup-message", contactId] });
    } catch (err: any) {
      toast.error("Failed to save", { description: err?.message?.substring(0, 100) });
    } finally {
      setIsSaving(false);
    }
  }, [messageText, selectedSender, contact, savedMessageId, campaignId, contactId, generationMeta, queryClient]);

  // ── Send (queues for HeyReach automation; auto-saves first if needed) ──
  const handleMarkSent = useCallback(async () => {
    if (!messageText.trim() || !selectedSender || !contact) return;
    setIsSaving(true);
    try {
      let messageId = savedMessageId;

      // Auto-persist when the rep hasn't clicked Save — sending the generated
      // copy as-is must not require a manual Save step first.
      if (!messageId) {
        const { data, error: insErr } = await supabase
          .from("outreach_messages")
          .insert({
            contact_id: contactId,
            contact_name: contact.full_name || "Contact",
            contact_title: contact.job_title || null,
            account_id: contact.company_id || null,
            account_name: contact.company_name || null,
            sender_id: selectedSender.id,
            sender_name: selectedSender.canonicalName,
            message_type: "linkedin_connection_followup",
            messaging_variant: variant,
            generated_text: messageText,
            final_text: messageText,
            status: "ready_to_send",
            campaign_id: campaignId || null,
            contact_linkedin_url: contact.linkedin_url || null,
            delivery_channel: "linkedin_automation",
            role_type_used: generationMeta?.marketingPersona ?? null,
            messaging_angle: generationMeta?.messagingAngle ?? null,
            account_category: generationMeta?.accountCategory ?? null,
            matrix_source: generationMeta?.messagingSource ? (String(generationMeta.messagingSource).includes("fallback") ? "fallback" : "matrix") : null,
            generation_metadata: (generationMeta || {}) as unknown as Json,
          })
          .select("id")
          .single();
        if (insErr) throw insErr;
        messageId = (data as unknown as { id: string }).id;
        setSavedMessageId(messageId);
      } else {
        const { error } = await supabase
          .from("outreach_messages")
          .update({
            status: "ready_to_send",
            delivery_channel: "linkedin_automation",
            final_text: messageText,
            sender_id: selectedSender.id,
            sender_name: selectedSender.canonicalName,
            updated_at: new Date().toISOString(),
          })
          .eq("id", messageId);
        if (error) throw error;
      }

      setSavedStatus("ready_to_send");
      toast.success("Queued for HeyReach");
      queryClient.invalidateQueries({ queryKey: ["linkedin-followup-message", contactId] });
    } catch (err: any) {
      toast.error("Failed to queue message", { description: err?.message?.substring(0, 100) });
    } finally {
      setIsSaving(false);
    }
  }, [savedMessageId, messageText, selectedSender, contact, variant, campaignId, contactId, generationMeta, queryClient]);

  // ── Copy ──
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(messageText);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  }, [messageText]);

  if (loadingMsg || loadingSenders || loadingCampaign) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // Never return null here: the modal renders this as its right-hand pane, so a null
  // produced a blank panel that was indistinguishable from the modal failing to open.
  if (!canAccessPanel) {
    return (
      <div className="h-full overflow-y-auto p-4 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <Linkedin className="h-4 w-4 text-blue-600" />
          LinkedIn Follow-Up
        </h3>
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{blockedReason}</span>
        </div>
      </div>
    );
  }

  if (!isAdminMode && connectionEnabled && connectionCheck === undefined) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!isAdminMode && !isMyConnection && !isMyCampaign) {
    // Read-only view for users who don't own this connection/campaign
    return (
      <div className="h-full overflow-y-auto p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Linkedin className="h-4 w-4 text-blue-600" />
            LinkedIn Follow-Up
          </h3>
          {savedStatus && <StatusBadgeSmall status={savedStatus} />}
        </div>
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <Eye className="h-3.5 w-3.5" />
          <span>This is another rep&apos;s connection. You can view but not generate or send messages.</span>
        </div>
        {existingMsg?.final_text || existingMsg?.generated_text ? (
          <Card>
            <CardContent className="px-4 py-3">
              <div className="text-sm whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-md p-3">
                {existingMsg.final_text || existingMsg.generated_text}
              </div>
            </CardContent>
          </Card>
        ) : (
          <p className="text-sm text-muted-foreground italic">No message drafted yet.</p>
        )}
      </div>
    );
  }

  const isSent = savedStatus === "sent" || savedStatus === "ready_to_send";

  return (
    <div className="flex flex-col lg:flex-row h-full min-h-0">
      {/* ── Compose workspace (right on desktop, first on mobile) ── */}
      <div className="flex-[3] min-w-0 overflow-y-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <Linkedin className="h-4 w-4 text-blue-600" />
          LinkedIn Follow-Up
        </h3>
        {savedStatus && <StatusBadgeSmall status={savedStatus} />}
      </div>

      {/* Admin Persona Switcher — only shown when there's a choice to make */}
      {isAdminMode && !isSent && senders.length > 1 && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-xs text-purple-700">
            <Shield className="h-3.5 w-3.5" />
            <span className="font-medium">Admin Mode — select sending persona</span>
          </div>
          <Select value={selectedSenderId} onValueChange={setSelectedSenderId}>
            <SelectTrigger className="h-9 border-purple-200 bg-purple-50/50">
              <SelectValue placeholder="Select persona..." />
            </SelectTrigger>
            <SelectContent>
              {senders.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!canSend && (
            <p className="text-[11px] text-purple-600/80">
              You can generate and save this draft. Only {selectedSender?.name ?? "the rep"} can
              send it, from their own LinkedIn account.
            </p>
          )}
        </div>
      )}

      {/* Sender (locked to current user's account for reps) */}
      {!isAdminMode && !isSent && userSender && (
        <div className="flex items-center gap-2 text-sm bg-blue-50 text-blue-800 border border-blue-200 rounded-md px-3 py-2">
          <Lock className="h-4 w-4 text-blue-600" />
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">{userSender.name}</span>
            <span className="text-[11px] text-blue-600/80">
              {isMyConnection ? "Locked · your connection" : "Locked · your campaign"}
            </span>
          </div>
        </div>
      )}

      {/* J5: soft warning when the rep is not a recorded LinkedIn connection. */}
      {!isAdminMode && !isSent && !isMyConnection && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            Not a recorded LinkedIn connection for this sender. You can still queue the follow-up — HeyReach sends it once the connection is accepted (or skips it if the connection never lands).
          </span>
        </div>
      )}

      {/* Generate Button */}
      {!isSent && (
        <Button
          onClick={handleGenerate}
          disabled={isGenerating || !selectedSender}
          className="w-full gap-2"
          variant="outline"
        >
          {isGenerating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {isGenerating ? "Generating..." : messageText ? "Regenerate" : "Generate Follow-Up"}
        </Button>
      )}

      {/* Message Editor / Display */}
      {messageText && (
        <Card>
          <CardHeader className="pb-2 pt-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Message ({charCount} chars)
              </CardTitle>
              {isOverLimit && (
                <Badge variant="destructive" className="text-[10px] gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Over {maxChars} — will fail on LinkedIn
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {isSent ? (
              <div className="text-sm whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-md p-3">
                {messageText}
              </div>
            ) : (
              <Textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="min-h-[160px] text-sm resize-none"
                placeholder="Message will appear here after generation..."
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {messageText && !isSent && (
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={isSaving || !messageText.trim() || isOverLimit}
            variant="outline"
            size="sm"
            className="gap-1.5 flex-1"
          >
            <Save className="h-3.5 w-3.5" />
            {isSaving ? "Saving..." : "Save Draft"}
          </Button>
          <Button
            onClick={handleCopy}
            variant="outline"
            size="sm"
            className="gap-1.5"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </Button>
          {canSend && (
            <Button
              onClick={handleMarkSent}
              disabled={isSaving || !messageText.trim() || isOverLimit}
              size="sm"
              className="gap-1.5 flex-1"
            >
              <Send className="h-3.5 w-3.5" />
              Send
            </Button>
          )}
        </div>
      )}

      {/* Sent confirmation */}
      {isSent && (
        <div className="flex gap-2">
          <Button onClick={handleCopy} variant="outline" size="sm" className="gap-1.5 flex-1">
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy Message"}
          </Button>
        </div>
      )}

      </div>

      {/* ── Context (left on desktop): willUse before a draft, used after ── */}
      <div className="flex-[2] min-w-0 overflow-y-auto bg-muted/5 lg:border-r lg:order-first">
        <MessageContextPane
          mode={generationMeta ? "used" : "willUse"}
          messagingVariant={generationMeta?.messagingVariant || variant}
          messagingAngle={generationMeta?.messagingAngle ?? sd?.messaging_angle ?? null}
          roleTypeUsed={generationMeta?.marketingPersona ?? contact?.marketing_persona ?? contact?.buyer_segment ?? null}
          accountCategory={generationMeta?.accountCategory ?? contact?.company_primary_category ?? null}
          matrixSource={generationMeta?.messagingSource || null}
          enrichmentContext={generationMeta ? null : willUseCtx}
          messageType="linkedin_connection_followup"
          deliveryChannel="linkedin_automation"
          senderName={selectedSender?.name || null}
          contactName={contact?.full_name || null}
          contactTitle={contact?.job_title || null}
          accountName={contact?.company_name || null}
          generationMetadata={generationMeta}
        />
      </div>
    </div>
  );
}

// ── Internal Helpers ─────────────────────────────────────────────────

function StatusBadgeSmall({ status }: { status: string }) {
  switch (status) {
    case "draft":
      return <Badge variant="outline" className="text-[11px]">Draft</Badge>;
    case "ready_to_send":
      return (
        <Badge variant="outline" className="text-[11px] bg-amber-50 text-amber-700 border-amber-200 gap-1">
          <Clock className="h-3 w-3" />Ready
        </Badge>
      );
    case "sent":
      return (
        <Badge variant="outline" className="text-[11px] bg-green-50 text-green-700 border-green-200 gap-1">
          <Check className="h-3 w-3" />Sent
        </Badge>
      );
    default:
      return <Badge variant="outline" className="text-[11px]">{status}</Badge>;
  }
}
